import { isNull, isUndefined } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import * as Sentry from '@sentry/react';
import { Loading } from 'components/AppLoader/Loading';
import { useCognitoUserContext, useCognitoUserContextWithUser } from 'domains/auth/AWSCognitoWrapper/context';
import { UserClientsContext, UserClientsCtx } from 'domains/auth/UserClientsWrapper/context';
import { FullClient, getClientRedirect } from 'domains/client';
import { useContactAndClients } from 'domains/contact/hooks';
import { useNavigation } from 'utils/hooks/useNavigation';
import { logClientDataError } from 'domains/contact/errors';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { LDContext } from 'utils/launchdarkly/launchdarkly';

type NavigationState = {
  from?: string;
  clientId?: string;
};
export type OnChangeActiveClient = (clientId: string, shouldRedirect?: boolean) => void;

const LoggedInWrapper: React.FC = ({ children }) => {
  const { isLoggedIn } = useCognitoUserContext();

  if (!isLoggedIn) {
    return (
      <UserClientsContext.Provider
        value={{
          isLoggedIn: false,
        }}
      >
        {children ?? <Outlet />}
      </UserClientsContext.Provider>
    );
  }

  return <UserDataWrapper>{children}</UserDataWrapper>;
};

const UserDataWrapper: React.FC = ({ children }) => {
  const LDClient = useLDClient();
  const { signOut, user } = useCognitoUserContextWithUser();
  const { data } = useContactAndClients({
    onError: e => {
      signOut();
      logClientDataError(e);
    },
    useErrorBoundary: true,
  });

  useEffect(() => {
    if (data) {
      Sentry.withScope(scope => {
        const user = scope.getUser();
        Sentry.setUser({
          ...user,
          username: data.contactId,
        });
      });

      LDClient?.identify({
        kind: LDContext.PORTAL_CLIENT,
        key: user.attributes.email,
        contactId: data.contactId,
        email: user.attributes.email,
        anonymous: false,

        application: 'portal',
      });
    }
  }, [data]);

  if (!data) {
    return <Loading className="transition-all duration-100 z-10" />;
  }

  return (
    <ProfilesWrapper clients={data.clients} contactId={data.contactId}>
      {children}
    </ProfilesWrapper>
  );
};

const getDefaultSelectedClientId = (clients: FullClient[]) => (clients.length === 1 ? clients[0].clientID : null);

const ProfilesWrapper: React.FC<{ clients: FullClient[]; contactId: string }> = ({ clients, contactId, children }) => {
  const [activeClientId, setActiveClientId] = useState<string | null>(getDefaultSelectedClientId(clients));

  const { signOut } = useCognitoUserContextWithUser();

  const { navigate } = useNavigation<NavigationState>();

  const activeClient = (activeClientId && clients.find(c => c.clientID === activeClientId)) || null;

  const onChangeClient = useCallback<OnChangeActiveClient>(
    clientId => {
      const isOtherClient = !isNull(activeClient) && clientId !== activeClient.clientID;
      const newClient = clients.find(c => c.clientID === clientId);

      if (isUndefined(newClient)) {
        throw new Error(
          `Tried to change client to id ${clientId} but it couldn't be found on the list of clients (length ${clients.length})`,
        );
      }

      setActiveClientId(newClient.clientID);

      if (isOtherClient) {
        // If switching clients, direct to default route for the given client (dashboard, intake, etc)
        navigate(getClientRedirect(newClient));
      }
    },
    [activeClient, clients, navigate],
  );

  useEffect(() => {
    Sentry.setTag('activeClientId', activeClientId);
  }, [activeClientId]);

  // Auto pick client if only 1
  useEffectOnce(() => {
    if (clients.length === 1) {
      onChangeClient(clients[0].clientID);
    }
  });

  const contextValue: UserClientsCtx =
    isNull(activeClient) || isNull(activeClientId)
      ? {
          isLoggedIn: true,
          isProfileSelected: false,
          activeClient: null,
          activeClientId: null,

          changeClient: onChangeClient,
          signOut,
          clients,
          contactId,
        }
      : {
          isLoggedIn: true,
          isProfileSelected: true,
          activeClient,
          activeClientId,

          changeClient: onChangeClient,
          signOut,
          clients,
          contactId,
        };

  return <UserClientsContext.Provider value={contextValue}>{children ?? <Outlet />}</UserClientsContext.Provider>;
};

export const UserClientsWrapper = LoggedInWrapper;
